import { useState } from 'react';
import * as ss58 from '@chainflip/utils/ss58';
import { type HexString } from '@chainflip/utils/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fromUnixTime } from 'date-fns';
import useOldFundingSDK from '@/shared/hooks/useOldFundingSdk';
import { useOrders } from './useOrders';
import { useExtrinsicSDK, useStateChainAccount } from './useStateChainAccount';

export type OffboardingStep =
  | 'unknown'
  | 'checklist'
  | 'deregister'
  | 'request-redemption'
  | 'execute-redemption'
  | 'complete';

export type OffboardingContext = ReturnType<typeof useOffboarding>;

export default function useOffboarding() {
  const { fundingSDK } = useOldFundingSDK();
  const { rpcOrders } = useOrders();
  const { isLoading, flipBalance, boostBalances, freeBalances, accountId, accountInfo } =
    useStateChainAccount();
  const { getExtrinsicSDK } = useExtrinsicSDK();
  const [shouldFetchRedemption, setShouldFetchRedemption] = useState(false);
  const [expectRedemptionRequest, setExpectRedemptionRequest] = useState(false);

  const { data: pendingRedemption = null, isLoading: redemptionLoading } = useQuery({
    queryKey: ['pendingRedemption', Boolean(fundingSDK), accountId],
    enabled: Boolean(fundingSDK && accountId),
    queryFn: async () =>
      (await fundingSDK!.getPendingRedemption(ss58.toPublicKey(accountId!))) ?? null,
    refetchInterval: shouldFetchRedemption ? 5_000 : undefined,
    select: (data) =>
      data && {
        amount: data.amount,
        redeemAddress: data.redeemAddress,
        startTime: fromUnixTime(Number(data.startTime)).valueOf(),
        expiryTime: fromUnixTime(Number(data.expiryTime)).valueOf(),
      },
  });

  const hasOrders = rpcOrders.length > 0;
  const hasBoostPositions = boostBalances && boostBalances.length > 0;
  const hasBalances = freeBalances && Object.values(freeBalances).some((balance) => balance.gt(0));
  const hasPendingRedemption = Boolean(pendingRedemption);

  const deregister = useMutation({
    mutationFn: async () => {
      await getExtrinsicSDK().deregisterAccount();
    },
  });

  const redeemAllFlip = useMutation({
    mutationFn: async (ethAddress: HexString) => {
      await getExtrinsicSDK().redeemFlip('Max', ethAddress);
      setShouldFetchRedemption(true);
      setExpectRedemptionRequest(false);
    },
  });

  const executeRedemption = useMutation({
    mutationFn: async () => {
      if (!fundingSDK) throw new Error('Funding SDK not initialized');
      if (!accountId) throw new Error('Account ID not available');
      if (!pendingRedemption) throw new Error('No pending redemption found');
      await fundingSDK.executeRedemption(ss58.toPublicKey(accountId));
      setShouldFetchRedemption(true);
      if (pendingRedemption.expiryTime < Date.now()) setExpectRedemptionRequest(true);
    },
  });

  let step: OffboardingStep = 'unknown';
  if (isLoading) {
    // do nothing
  } else if (hasOrders || hasBoostPositions || hasBalances) {
    step = 'checklist';
  } else if (accountInfo?.role === 'liquidity_provider') {
    step = 'deregister';
  } else if (!hasPendingRedemption && (flipBalance?.gt(0) || expectRedemptionRequest)) {
    if (redemptionLoading) {
      step = 'unknown';
    } else {
      step = 'request-redemption';
    }
  } else if (!executeRedemption.isSuccess && (hasPendingRedemption || redeemAllFlip.isSuccess)) {
    step = 'execute-redemption';
  } else if (executeRedemption.isSuccess && !expectRedemptionRequest) {
    step = 'complete';
  }

  return {
    step,
    hasOrders,
    hasBoostPositions,
    hasBalances,
    deregister,
    redeemAllFlip,
    executeRedemption,
    pendingRedemption,
    redemptionLoading,
  };
}
